import {
  TaskTrackerBoardSticker,
  TaskTrackerFilter,
  TaskTrackerFilterSelectOption,
  TaskTrackerTaskStickerValue,
} from '../store/taskTracker.types'
import { DEFAULT_TASK_TRACKER_FILTER_SELECT_OPTION_COLOR } from '../lib/taskTrackerFilter.constants'
import {
  ApiTaskCardSticker,
  ApiTaskTrackerBoardSticker,
  ApiTaskTrackerFilter,
  ApiTaskTrackerFilterOption,
} from './taskTracker.api.types'
import { ensureApiArray } from './taskTracker.api.mappers.utils'
import {
  extractCrmLeadStickerFunnelId,
  extractCrmLeadStickerLeadId,
  isTaskTrackerCrmLeadSticker,
  normalizeTaskTrackerStickerApiValue,
} from '../lib/taskTrackerCrmLeadSticker.utils'

export const mapFilterOptionFromApi = (
  item: ApiTaskTrackerFilterOption,
): TaskTrackerFilterSelectOption | null => {
  const label = String(item.label ?? '').trim()
  if (!label) return null

  const color = String(item.color ?? '').trim()
  return {
    label,
    color: /^#[0-9a-f]{6}$/i.test(color)
      ? color
      : DEFAULT_TASK_TRACKER_FILTER_SELECT_OPTION_COLOR,
  }
}

export const mapFilterOptionsFromApi = (
  items: ApiTaskTrackerFilterOption[] | null | undefined,
): TaskTrackerFilterSelectOption[] =>
  ensureApiArray(items)
    .map(mapFilterOptionFromApi)
    .filter((item): item is TaskTrackerFilterSelectOption => item != null)

export const mapFilterFromApi = (item: ApiTaskTrackerFilter): TaskTrackerFilter => ({
  id: Number(item.id),
  fieldType: String(item.field_type ?? '').trim(),
  title: String(item.title ?? '').trim(),
  color: String(item.color ?? '').trim(),
  isRequired: Boolean(item.is_required),
  position: Number(item.position ?? item.sort_order ?? 0),
  options: mapFilterOptionsFromApi(item.options),
  isSystem: Boolean(item.is_system),
  systemKey: item.system_key ? String(item.system_key).trim() : null,
})

export const mapFiltersFromApi = (
  items: ApiTaskTrackerFilter[] | null | undefined,
): TaskTrackerFilter[] =>
  ensureApiArray(items)
    .map(mapFilterFromApi)
    .sort((a, b) => a.position - b.position || a.id - b.id)

export const mapBoardStickerFromApi = (
  item: ApiTaskTrackerBoardSticker,
): TaskTrackerBoardSticker => ({
  id: Number(item.id),
  boardId: Number(item.board_id),
  fieldSettingId: Number(item.field_setting_id),
  fieldType: String(item.field_type ?? '').trim(),
  title: String(item.title ?? '').trim(),
  color: String(item.color ?? '').trim(),
  position: Number(item.position ?? 0),
  isVisible: Boolean(item.is_visible),
  isRequired: Boolean(item.is_required),
  catalogIsRequired: Boolean(item.catalog_is_required),
  isSystem: Boolean(item.is_system),
  systemKey: item.system_key ? String(item.system_key).trim() : null,
  canEdit: item.can_edit !== false,
  canManageRequired: item.can_manage_required !== false,
  options: mapFilterOptionsFromApi(item.options),
})

export const mapBoardStickersFromApi = (
  items: ApiTaskTrackerBoardSticker[] | null | undefined,
): TaskTrackerBoardSticker[] =>
  ensureApiArray(items)
    .map(mapBoardStickerFromApi)
    .sort((a, b) => a.position - b.position || a.id - b.id)

export const mapTaskStickerFromApi = (
  item: ApiTaskCardSticker,
): TaskTrackerTaskStickerValue => {
  const fieldType = String(item.field_type ?? '').trim()
  const systemKey = item.system_key ? String(item.system_key).trim() : null
  const isCrmLead = isTaskTrackerCrmLeadSticker(fieldType, systemKey)

  let value: string | null = null
  if (fieldType === 'deadline') {
    value = item.value == null ? null : String(item.value)
  } else if (fieldType !== 'performer') {
    value = normalizeTaskTrackerStickerApiValue(
      fieldType,
      systemKey,
      item.value,
    )
  }

  return {
    fieldSettingId: Number(item.field_setting_id),
    fieldType,
    title: String(item.title ?? '').trim(),
    color: String(item.color ?? '').trim(),
    isVisible: Boolean(item.is_visible),
    isRequired: Boolean(item.is_required),
    value,
    systemKey,
    isSystem: Boolean(item.is_system),
    ...(isCrmLead
      ? {
          crmLeadId: extractCrmLeadStickerLeadId(item.value),
          crmFunnelId: extractCrmLeadStickerFunnelId(item.value),
        }
      : {}),
  }
}

export const mapTaskStickersFromApi = (
  items: ApiTaskCardSticker[] | null | undefined,
): TaskTrackerTaskStickerValue[] =>
  ensureApiArray(items).map(mapTaskStickerFromApi)
